import React from 'react';

export default function ResponsibleUseFr() {
  return (
    <div className="doc-page">
      <div className="doc-header">
        <span className="doc-label">Recouvrements</span>
        <h1 className="doc-title">
          Avis d'utilisation responsable et de recouvrement de MegaTransact
        </h1>
        <p className="doc-intro">
          Comment MegaTransact aborde l'utilisation responsable, le suivi des remboursements et
          les activités de recouvrement. MegaTransact est une plateforme numérique commençant par
          le prêt salarial et s'étendant progressivement à des services financiers pratiques.
        </p>
        <div className="doc-meta">
          <span className="doc-meta-item">
            <strong>Date d'entrée en vigueur :</strong> 27 mars 2026
          </span>
          <span className="doc-meta-item">
            <strong>Dernière mise à jour :</strong> 27 mars 2026
          </span>
        </div>
      </div>

      <div className="doc-body">
        <div className="doc-preamble">
          <p>
            Le présent avis d'utilisation responsable et de recouvrement s'applique aux
            utilisateurs des services MegaTransact, en particulier aux utilisateurs du service de
            prêt salarial de MegaTransact. Il doit être lu conjointement avec les conditions
            générales d'utilisation de MegaTransact, la politique de confidentialité de
            MegaTransact et les conditions de prêt salarial de MegaTransact.
          </p>
        </div>

        {/* Section 1 */}
        <div className="doc-section">
          <span className="section-number">Section 1</span>
          <h2 className="section-title">Objet du présent avis</h2>
          <p>
            MegaTransact vise à fournir un accès structuré et responsable au soutien financier
            lié au salaire. Le présent avis explique :
          </p>
          <ul>
            <li>les normes que les utilisateurs sont censés respecter</li>
            <li>comment MegaTransact encourage l'utilisation responsable</li>
            <li>ce qui se passe lorsqu'un remboursement est manqué ou retardé</li>
            <li>
              comment MegaTransact peut communiquer avec les utilisateurs et les canaux liés au
              lieu de travail lorsque cela est légal et approprié
            </li>
            <li>
              l'approche générale de MegaTransact en matière de rappels, de restrictions et
              d'activités de récupération
            </li>
          </ul>
        </div>

        {/* Section 2 */}
        <div className="doc-section">
          <span className="section-number">Section 2</span>
          <h2 className="section-title">Utilisation responsable de MegaTransact</h2>
          <p>
            Les utilisateurs sont censés utiliser MegaTransact de manière responsable, honnête
            et conformément à l'objectif prévu de la plateforme.
          </p>
          <p>L'utilisation responsable comprend :</p>
          <ul>
            <li>fournir des informations exactes et complètes</li>
            <li>
              ne demander que le soutien que l'utilisateur s'attend raisonnablement à gérer
            </li>
            <li>examiner attentivement les conditions de remboursement avant l'acceptation</li>
            <li>maintenir les détails du compte et les coordonnées à jour</li>
            <li>
              répondre aux demandes de vérification et de support en temps opportun
            </li>
            <li>
              éviter l'utilisation abusive, la tromperie, les comptes en double ou les demandes
              manipulées
            </li>
            <li>
              contacter MegaTransact rapidement si les circonstances changent
            </li>
          </ul>
          <p>
            MegaTransact peut tenir compte de l'historique d'utilisation responsable lors de
            l'évaluation de l'accès futur aux services.
          </p>
        </div>

        {/* Section 3 */}
        <div className="doc-section">
          <span className="section-number">Section 3</span>
          <h2 className="section-title">
            Approche de MegaTransact en matière d'administration responsable des services
          </h2>
          <p>
            MegaTransact peut appliquer des garde-fous internes conçus pour soutenir l'utilisation
            responsable des services et réduire la pression évitable sur les remboursements.
          </p>
          <p>Ces garde-fous peuvent inclure :</p>
          <ul>
            <li>vérifications d'éligibilité</li>
            <li>vérification d'identité et d'emploi</li>
            <li>évaluation liée au salaire</li>
            <li>règles internes de plafonnement des remboursements</li>
            <li>contrôles d'examen et d'approbation des demandes</li>
            <li>limites de service basées sur le comportement antérieur sur la plateforme</li>
            <li>
              restrictions temporaires ou permanentes en cas de préoccupations d'utilisation
              abusive ou de risque
            </li>
          </ul>
          <p>
            Ces mesures visent à soutenir une administration de service prudente et durable.
          </p>
        </div>

        {/* Section 4 */}
        <div className="doc-section">
          <span className="section-number">Section 4</span>
          <h2 className="section-title">Attentes de remboursement</h2>
          <p>
            Lorsqu'un utilisateur accepte un soutien salarial via MegaTransact, l'utilisateur est
            censé respecter les conditions de remboursement qui ont été présentées et acceptées.
          </p>
          <p>Les utilisateurs doivent :</p>
          <ul>
            <li>comprendre le montant total de remboursement avant l'acceptation</li>
            <li>
              comprendre les dates de remboursement ou la structure de remboursement liée au
              salaire
            </li>
            <li>
              s'assurer de rester joignables via les coordonnées fournies
            </li>
            <li>
              notifier MegaTransact lorsque les circonstances pertinentes d'emploi ou de
              remboursement changent de manière significative
            </li>
          </ul>
          <p>
            Les obligations de remboursement restent en vigueur sauf si MegaTransact confirme
            formellement un changement, une renonciation, une restructuration ou une clôture.
          </p>
        </div>

        {/* Section 5 */}
        <div className="doc-section">
          <span className="section-number">Section 5</span>
          <h2 className="section-title">Lorsqu'un remboursement est manqué ou retardé</h2>
          <p>
            Un remboursement peut être considéré comme manqué, retardé ou échoué lorsque :
          </p>
          <ul>
            <li>le paiement n'est pas reçu à temps</li>
            <li>
              une retenue sur salaire liée à la paie ne se produit pas comme prévu
            </li>
            <li>
              une obligation liée au décaissement reste impayée
            </li>
            <li>
              un paiement est annulé, rejeté, bloqué ou autrement échoué
            </li>
            <li>
              un utilisateur évite ou ignore le suivi raisonnable du remboursement
            </li>
          </ul>
          <p>
            Un remboursement manqué ou retardé ne supprime pas automatiquement l'obligation. Le
            montant impayé peut rester dû, sauf accord contraire par écrit ou via un processus
            approuvé de MegaTransact.
          </p>
        </div>

        {/* Section 6 */}
        <div className="doc-section">
          <span className="section-number">Section 6</span>
          <h2 className="section-title">Processus de rappel et de suivi</h2>
          <p>
            Si un remboursement est manqué ou retardé, MegaTransact peut prendre une ou plusieurs
            mesures de suivi raisonnables, y compris :
          </p>
          <ul>
            <li>envoyer des rappels via l'application</li>
            <li>
              envoyer des SMS, e-mails, WhatsApp ou autres communications approuvées
            </li>
            <li>appeler l'utilisateur le cas échéant</li>
            <li>
              notifier l'utilisateur du statut impayé et des prochaines étapes
            </li>
            <li>demander des clarifications ou des informations mises à jour</li>
            <li>
              appliquer des restrictions temporaires à l'accès au compte ou aux futures demandes
            </li>
          </ul>
          <p>
            MegaTransact vise à utiliser un ton mesuré et professionnel dans les communications
            de suivi.
          </p>
        </div>

        {/* Section 7 */}
        <div className="doc-section">
          <span className="section-number">Section 7</span>
          <h2 className="section-title">Suivi lié au lieu de travail</h2>
          <p>
            Étant donné que MegaTransact commence par le prêt salarial, certains arrangements de
            remboursement peuvent être liés à un employeur ou un processus sur le lieu de travail.
          </p>
          <p>
            Lorsque cela est légal, contractuellement soutenu et opérationnellement pertinent,
            MegaTransact peut communiquer avec les canaux liés à l'employeur à des fins telles
            que :
          </p>
          <ul>
            <li>confirmer le calendrier de paie</li>
            <li>confirmer le statut d'emploi</li>
            <li>
              suivre les processus de remboursement liés à l'administration des salaires
            </li>
            <li>
              résoudre les problèmes administratifs affectant les retenues ou les remboursements
            </li>
          </ul>
          <p>
            MegaTransact visera à limiter ces contacts à ce qui est raisonnablement nécessaire
            pour l'administration du service, la gestion des remboursements, la prévention de la
            fraude ou la récupération légale.
          </p>
        </div>

        {/* Section 8 */}
        <div className="doc-section">
          <span className="section-number">Section 8</span>
          <h2 className="section-title">Restrictions de service après non-paiement</h2>
          <p>
            Si un remboursement reste impayé, retardé ou non résolu, MegaTransact peut restreindre
            l'accès à certaines ou toutes les fonctionnalités de la plateforme, y compris :
          </p>
          <ul>
            <li>bloquer les nouvelles demandes</li>
            <li>suspendre l'accès au prêt salarial</li>
            <li>limiter la fonctionnalité du compte</li>
            <li>placer le compte sous examen manuel</li>
            <li>
              exiger une vérification supplémentaire avant l'utilisation future du service
            </li>
          </ul>
          <p>
            Les restrictions peuvent rester en place jusqu'à ce que le problème soit résolu à la
            satisfaction de MegaTransact.
          </p>
        </div>

        {/* Section 9 */}
        <div className="doc-section">
          <span className="section-number">Section 9</span>
          <h2 className="section-title">Activité de recouvrement</h2>
          <p>
            Lorsqu'un montant reste impayé, MegaTransact peut prendre des mesures de récupération
            légales et proportionnées.
          </p>
          <p>Celles-ci peuvent inclure :</p>
          <ul>
            <li>des rappels et avis répétés</li>
            <li>la restriction ou la suspension du compte</li>
            <li>un suivi structuré du remboursement</li>
            <li>
              un suivi administratif lié à l'employeur lorsque cela est légal et applicable
            </li>
            <li>le renvoi à un support de récupération interne ou externe</li>
            <li>
              l'utilisation de canaux d'exécution ou de résolution de litiges légaux si nécessaire
            </li>
          </ul>
          <p>
            MegaTransact ne s'engage pas à prendre toutes les mesures dans chaque cas et peut
            déterminer la réponse appropriée en fonction des circonstances.
          </p>
        </div>

        {/* Section 10 */}
        <div className="doc-section">
          <span className="section-number">Section 10</span>
          <h2 className="section-title">Coopération de l'utilisateur</h2>
          <p>
            Les utilisateurs sont censés coopérer de bonne foi lorsque des problèmes de
            remboursement surviennent.
          </p>
          <p>Cela comprend :</p>
          <ul>
            <li>répondre aux communications</li>
            <li>confirmer les faits pertinents honnêtement</li>
            <li>éviter les fausses déclarations ou les conduites évasives</li>
            <li>suivre les processus de remboursement ou de résolution approuvés</li>
            <li>
              ne pas interférer avec les contrôles de la plateforme, les rappels ou les
              vérifications
            </li>
          </ul>
          <p>
            Le défaut de coopération peut affecter l'éligibilité aux services futurs et peut
            augmenter la probabilité d'une action de récupération formelle.
          </p>
        </div>

        {/* Section 11 */}
        <div className="doc-section">
          <span className="section-number">Section 11</span>
          <h2 className="section-title">Fraude, abus et évasion délibérée</h2>
          <p>
            MegaTransact traite avec sérieux la fraude, la tromperie, la falsification de
            documents, l'usurpation d'identité, la manipulation de comptes et l'évasion délibérée
            des remboursements.
          </p>
          <p>Lorsque MegaTransact soupçonne raisonnablement une inconduite, il peut :</p>
          <ul>
            <li>suspendre ou résilier l'accès</li>
            <li>conserver les registres pertinents</li>
            <li>rejeter les demandes en attente</li>
            <li>escalader l'affaire en interne</li>
            <li>
              faire appel à un soutien lié au lieu de travail ou à des tiers lorsque cela est
              légal
            </li>
            <li>
              signaler l'affaire aux autorités compétentes lorsque la loi l'exige ou le justifie
            </li>
          </ul>
        </div>

        {/* Section 12 */}
        <div className="doc-section">
          <span className="section-number">Section 12</span>
          <h2 className="section-title">Pas de renonciation par retard</h2>
          <p>
            Si MegaTransact n'exerce pas immédiatement un droit, n'envoie pas d'avis ou ne
            commence pas d'activité de recouvrement, cela ne signifie pas que l'obligation a été
            annulée ou abandonnée.
          </p>
          <p>
            Toute renonciation, règlement, variation ou clôture doit être expressément confirmé
            par MegaTransact via un processus autorisé.
          </p>
        </div>

        {/* Section 13 */}
        <div className="doc-section">
          <span className="section-number">Section 13</span>
          <h2 className="section-title">Tenue de registres</h2>
          <p>MegaTransact peut conserver des registres relatifs à :</p>
          <ul>
            <li>les calendriers de remboursement</li>
            <li>les soldes impayés</li>
            <li>l'historique des rappels</li>
            <li>les communications envoyées ou reçues</li>
            <li>le suivi lié à l'employeur le cas échéant</li>
            <li>les mesures de récupération prises</li>
            <li>les restrictions placées sur le compte</li>
            <li>l'historique de litige ou de résolution</li>
          </ul>
          <p>
            Ces registres peuvent être utilisés à des fins opérationnelles, juridiques, d'audit,
            de résolution de litiges, de prévention de la fraude et de conformité, sous réserve
            de la loi applicable.
          </p>
        </div>

        {/* Section 14 */}
        <div className="doc-section">
          <span className="section-number">Section 14</span>
          <h2 className="section-title">Confidentialité et traitement des données</h2>
          <p>
            Les informations utilisées dans l'examen de l'utilisation responsable et les activités
            de recouvrement seront traitées conformément à la politique de confidentialité de
            MegaTransact.
          </p>
          <p>
            Cela peut inclure l'utilisation d'informations personnelles, techniques, liées à
            l'emploi, liées au salaire et liées au remboursement lorsque cela est raisonnablement
            nécessaire pour :
          </p>
          <ul>
            <li>l'administration du service</li>
            <li>la vérification</li>
            <li>la gestion du remboursement</li>
            <li>la prévention de la fraude</li>
            <li>les activités de recouvrement</li>
            <li>le traitement des litiges</li>
            <li>la conformité et les fins de sécurité</li>
          </ul>
        </div>

        {/* Section 15 */}
        <div className="doc-section">
          <span className="section-number">Section 15</span>
          <h2 className="section-title">Modifications du présent avis</h2>
          <p>
            MegaTransact peut mettre à jour le présent avis d'utilisation responsable et de
            recouvrement de temps à autre pour refléter le perfectionnement des services, les
            améliorations opérationnelles, les exigences légales ou les changements dans les
            processus de récupération.
          </p>
          <p>
            Lorsque des mises à jour importantes sont effectuées, MegaTransact peut informer les
            utilisateurs via l'application, le site web, l'e-mail ou d'autres canaux appropriés.
          </p>
        </div>

        {/* Section 16 */}
        <div className="doc-section">
          <span className="section-number">Section 16</span>
          <h2 className="section-title">Nous contacter</h2>
          <p>
            Si vous avez des questions, des demandes ou des préoccupations concernant le présent
            avis d'utilisation responsable et de recouvrement, veuillez contacter :
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>MegaTransact</strong>
            </p>
            <p>
              <strong>E-mail :</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Société :</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Adresse :</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
